// Phase 4: 委任UIの判定純関数。依存ゼロ（scripts/test-delegation-core.mjs で単体コンパイルする）。
// Rust 側の正: service/agent_jobs.rs infer_decision_action（おすすめ/推奨 + A-D 規約）/
//             commands/agents.rs（mission_id "strategy:<YYYY-MM-DD>" 規約）

export type DelegateProvider = "codex" | "claude";
export type DecisionAction = "A" | "B" | "C" | "D";

export interface SuggestionOwnerLike {
  owner: string;
}

/** インボックス行でデフォルト強調する実行ボタン。
 *  AI提案（無ければタスクの確定 owner）が codex/claude のときだけ返す。
 *  taiga/grok/hermes は実行経路を作らない（記録のみ）ので null。 */
export function delegationDefaultProvider(
  suggestion: SuggestionOwnerLike | null,
  taskOwner: string
): DelegateProvider | null {
  const candidate = suggestion?.owner || taskOwner;
  return candidate === "codex" || candidate === "claude" ? candidate : null;
}

export interface DecisionSourceLike {
  status: string;
  result: string;
}

/** 結果カードの A/B/C/D デフォルト強調。
 *  needs_decision のみ「おすすめ/推奨」行の最初の A〜D（無ければ A = Rust infer_decision_action と同じ既定）。
 *  done は提案なし（null）。 */
export function suggestedDecisionAction(job: DecisionSourceLike): DecisionAction | null {
  if (job.status !== "needs_decision") return null;
  for (const raw of job.result.split("\n")) {
    const line = raw.replace(/^[-*#\s`]+/, "");
    if (line.startsWith("おすすめ") || line.startsWith("推奨")) {
      const m = line.match(/[A-D]/);
      if (m) return m[0] as DecisionAction;
    }
  }
  return "A";
}

export interface ChainJobLike {
  id: string;
  parentJobId: string | null;
}

/** 親を辿った深さ（起点=0）。表示窓外の親欠落・循環でも安全に打ち切る。 */
export function chainDepth(job: ChainJobLike, jobs: ChainJobLike[]): number {
  const byId = new Map(jobs.map((j) => [j.id, j]));
  const seen = new Set<string>([job.id]);
  let depth = 0;
  let current: ChainJobLike | undefined = job;
  while (current && current.parentJobId) {
    depth += 1;
    const parent = byId.get(current.parentJobId);
    if (!parent || seen.has(parent.id)) break;
    seen.add(parent.id);
    current = parent;
  }
  return depth;
}

/** 連鎖バッジのラベル。起点ジョブは null。 */
export function chainLabel(job: ChainJobLike, jobs: ChainJobLike[]): string | null {
  if (!job.parentJobId) return null;
  const depth = chainDepth(job, jobs);
  return depth >= 2 ? `↳ 続き ×${depth}` : "↳ 続き";
}

export interface StrategyJobLike {
  missionId: string | null;
  status: string;
  result: string;
  createdAt: string;
}

/** 今日の作戦ジョブ（missionId = "strategy:<todayKey>"）の最新1件。
 *  todayKey は必ず todayLocalKey()（ローカル日付）を渡す。 */
export function latestStrategyJob<T extends StrategyJobLike>(
  jobs: T[],
  todayKey: string
): T | null {
  const key = `strategy:${todayKey}`;
  const todays = jobs.filter((job) => job.missionId === key);
  if (todays.length === 0) return null;
  return todays.reduce((a, b) => (a.createdAt >= b.createdAt ? a : b));
}

/** ローカル日付 YYYY-MM-DD（commandCore todayKey() と同一規約。toISOString は UTC になるため禁止）。 */
export function todayLocalKey(now: Date = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
    now.getDate()
  ).padStart(2, "0")}`;
}
